import { useEffect, useState } from "react";
import {
  PlatformDashboardService,
  PlatformDashboardVM,
} from "@propertelligent/client-api";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import apiHelper from "../../utils/apiHelper";
import CardContainer from "../../newDashboard/CardContainer";
import { GetImages } from "../../utils/GetImages";
import SimpleSpinner from "../loader";
import "../../newDashboard/dashboard.css";
import "./platform-portal.css";

const DONUT_COLORS = ["#1A56DB", "#0DBF6F", "#F79009", "#7A5AF8", "#F04438", "#12B76A"];

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatNumber = (value?: number) => {
  if (value === undefined || value === null) return "0";
  return new Intl.NumberFormat("en-US").format(value);
};

const PlatformDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PlatformDashboardVM | null>(null);

  const fetchDashboard = async () => {
    setLoading(true);
    const res = await apiHelper(
      PlatformDashboardService.getApiPlatformDashboard,
      { showNotification: false }
    );
    if (res?.data?.success) {
      setData(res.data.success);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="platform-loading">
        <SimpleSpinner isFullPage />
      </div>
    );
  }

  const revenueSeries = (data?.revenueLast12Months || []).map((m) => ({
    month: m.month,
    amount: m.amount ?? 0,
  }));
  const usersSeries = (data?.activeUsersLast30Days || []).map((d) => ({
    day: d.day,
    count: d.count ?? 0,
  }));
  const subscriptionSeries = (data?.subscriptionTypeBreakdown || []).map((s) => ({
    name: s.type || "Unknown",
    value: s.count ?? 0,
  }));

  return (
    <div className="platform-portal-page">
      <div className="top-listing-header-top">
        <h2 className="H2">Platform Overview</h2>
      </div>

      {/* Stat tiles */}
      <div className="platform-stat-grid">
        <CardContainer
          heading={formatNumber(data?.organizationCount)}
          sub_data="Organizations"
          image={GetImages.propertiesIconMenu}
        />
        <CardContainer
          heading={formatNumber(data?.activeSubscriptionCount)}
          sub_data="Active Subscriptions"
          image={GetImages.subscriptionSidebarIcon}
        />
        <CardContainer
          heading={formatNumber(data?.trialSubscriptionCount)}
          sub_data="Trial Subscriptions"
          image={GetImages.subscriptionSidebarIcon}
        />
        <CardContainer
          heading={formatNumber(data?.payAsYouGoSubscriptionCount)}
          sub_data="Pay-As-You-Go"
          image={GetImages.subscriptionSidebarIcon}
        />
        <CardContainer
          heading={data?.formatted?.MonthlyRecurringRevenue || formatCurrency(data?.monthlyRecurringRevenue)}
          sub_data="Monthly Recurring Revenue"
          image={GetImages.accountingIconMenu}
        />
        <CardContainer
          heading={data?.formatted?.RevenueThisMonth || formatCurrency(data?.revenueThisMonth)}
          sub_data="Revenue This Month"
          image={GetImages.accountingIconMenu}
        />
        <CardContainer
          heading={formatNumber(data?.totalProperties)}
          sub_data="Total Properties"
          image={GetImages.propertiesIconMenu}
        />
        <CardContainer
          heading={formatNumber(data?.totalUnits)}
          sub_data="Total Units"
          image={GetImages.unitIconMenu}
        />
        <CardContainer
          heading={formatNumber(data?.totalTenants)}
          sub_data="Total Tenants"
          image={GetImages.tenantIconMenu}
        />
        <CardContainer
          heading={`${(data?.occupancyRate ?? 0).toFixed(1)}%`}
          sub_data={`Occupancy (${formatNumber(data?.occupiedUnits)}/${formatNumber(data?.totalUnits)})`}
          image={GetImages.unitIconMenu}
        />
        <CardContainer
          heading={formatNumber(data?.activeDailyUsers)}
          sub_data="Active Daily Users"
          image={GetImages.tenantIconMenu}
        />
        <CardContainer
          heading={formatNumber(data?.openSupportRequests)}
          sub_data="Open Support Requests"
          image={GetImages.serviceRequestIconMenu}
        />
      </div>

      {/* Charts */}
      <div className="platform-chart-row">
        <div className="platform-chart-card">
          <h5 className="section-title black">Revenue (Last 12 Months)</h5>
          {revenueSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revenueFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#1A56DB" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#1A56DB" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Area
                  type="monotone"
                  dataKey="amount"
                  name="Revenue"
                  stroke="#1A56DB"
                  strokeWidth={2}
                  fill="url(#revenueFill)"
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <p className="normal_text grey_subtext1">No revenue data available.</p>
          )}
        </div>

        <div className="platform-chart-card platform-donut-card">
          <h5 className="section-title black">Subscription Breakdown</h5>
          {subscriptionSeries.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={subscriptionSeries}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="60%"
                  outerRadius="90%"
                  paddingAngle={2}
                >
                  {subscriptionSeries.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={DONUT_COLORS[index % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="normal_text grey_subtext1">No subscription data available.</p>
          )}
          <div className="platform-donut-legend">
            {subscriptionSeries.map((s, index) => (
              <div key={index} className="platform-legend-item">
                <span
                  className="platform-legend-dot"
                  style={{ backgroundColor: DONUT_COLORS[index % DONUT_COLORS.length] }}
                />
                <span className="normal_text">{s.name}</span>
                <span className="normal_text grey_subtext1">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="platform-chart-card">
        <h5 className="section-title black">Active Users (Last 30 Days)</h5>
        {usersSeries.length > 0 ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={usersSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="count"
                name="Active Users"
                stroke="#0DBF6F"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="normal_text grey_subtext1">No active-user data available.</p>
        )}
      </div>
    </div>
  );
};

export default PlatformDashboard;
